import {
  type CreateReference,
  createExtensionValue,
  ExtensionUrl,
  filterReferencesByType,
  findExtensionValue,
  PatientStatusCode,
  updateExtensions,
  valsOrNone,
} from '@quromedical/fhir-common';
import type { PatientCall } from '../call';

import { isValidReference } from '../mappings';
import {
  mapFhirPersonToContactModel,
  mapFhirPersonToGeneralModel,
  mapPatientContactToFhirPartial,
  mapPersonGeneralToFhirPerson,
  type PersonContact,
  type PersonGeneral,
} from '../person';
import type { WithId } from '../types';
import type { GeneralPractitioners, PatientSummary } from './schema';

const statusActiveMap: Record<PatientStatusCode, boolean> = {
  [PatientStatusCode.Onboarded]: true,
  [PatientStatusCode.Admitted]: true,
  [PatientStatusCode.Discharged]: false,
  [PatientStatusCode.Invalidated]: false,
};

export const setPatientStatus = (
  patient: fhir4.Patient,
  status: PatientStatusCode,
): fhir4.Patient => {
  const extension = updateExtensions(
    ExtensionUrl.latestStatus,
    patient.extension,
    [createExtensionValue(ExtensionUrl.latestStatus)(status)],
  );

  return {
    ...patient,
    active: statusActiveMap[status],
    extension,
  };
};

/**
 * Update a patient's latest status as well as update the active/inactive state as appropriate
 */
export const updatePatientStatus = (
  patient: WithId<fhir4.Patient>,
  status: PatientStatusCode,
): WithId<fhir4.Patient> => {
  const update = setPatientStatus(patient, status);

  return {
    id: patient.id,
    ...update,
  };
};

/**
 * Onboarded patients are inactive with the intent that an admission will be created for the patient
 */
export const mapOnboardModelToFhirPatient = (
  general: Partial<PersonGeneral>,
  contact: Partial<PersonContact>,
): fhir4.Patient => {
  const generalPartial = mapPersonGeneralToFhirPerson(general);
  const contactPartial = mapPatientContactToFhirPartial(contact);

  const partialPatient: fhir4.Patient = {
    ...generalPartial,
    ...contactPartial,
    resourceType: 'Patient',
  };

  const withStatus = setPatientStatus(
    partialPatient,
    PatientStatusCode.Onboarded,
  );

  const extension = updateExtensions(
    ExtensionUrl.latestStatus,
    withStatus.extension,
    [
      createExtensionValue(ExtensionUrl.latestStatus)(
        PatientStatusCode.Onboarded,
      ),
    ],
  );

  return {
    ...withStatus,
    extension,
  };
};

/**
 * Update the assigned care team for a patient. Practitioners are mapped to the
 * `generalPractitioner` field, Organizations are in the `assignedOrganization` extension
 */
export const updatePatientCareTeam = (
  patient: WithId<fhir4.Patient>,
  assignedPractitioners: CreateReference[],
  assignedOrganizations: CreateReference[],
): WithId<fhir4.Patient> => {
  const updatedPatient: WithId<fhir4.Patient> = {
    ...patient,
    generalPractitioner: valsOrNone([
      ...assignedPractitioners,
      ...assignedOrganizations,
    ]),
  };

  return updatedPatient;
};

export const getPatientCareTeam = (
  patient: fhir4.Patient,
): GeneralPractitioners => {
  const practitionersReferences = filterReferencesByType(
    'Practitioner',
    patient.generalPractitioner,
  );
  const organizationsReferences = filterReferencesByType(
    'Organization',
    patient.generalPractitioner,
  );

  const practitioners = practitionersReferences.filter(isValidReference);
  const organizations = organizationsReferences.filter(isValidReference);
  return { practitioners, organizations };
};

export const getPatientStatus = (
  patient: WithId<fhir4.Patient>,
): PatientStatusCode | undefined =>
  findExtensionValue(ExtensionUrl.latestStatus)(patient.extension) as
    | PatientStatusCode
    | undefined;

export const mapFhirToSummaryModelPatient = (
  fhir: WithId<fhir4.Patient>,
  call?: PatientCall,
): WithId<PatientSummary> => {
  const general = mapFhirPersonToGeneralModel(fhir);
  const contact = mapFhirPersonToContactModel(fhir);

  const status = getPatientStatus(fhir);

  const practitionersReferences = filterReferencesByType(
    'Practitioner',
    fhir.generalPractitioner,
  );
  const organizationsReferences = filterReferencesByType(
    'Organization',
    fhir.generalPractitioner,
  );

  const practitioners = practitionersReferences.filter(isValidReference);
  const organizations = organizationsReferences.filter(isValidReference);

  return {
    id: fhir.id,
    status,
    contact,
    call,
    general,
    generalPractitioner: {
      organizations,
      practitioners,
    },
  };
};
